import React from 'react';
import PropTypes from 'prop-types';
import exact from 'prop-types-exact';
import { compose, lifecycle } from 'recompose';
import { connect } from 'react-redux';
import { reduxForm, formPropTypes, SubmissionError, FieldArray } from 'redux-form';
import {
  Table,
  Button,
  ButtonToolbar,
} from 'react-bootstrap';
import _ from 'lodash';
import { formProps } from '../../../propTypes';

import UserBonusTicketTable from './UserBonusTicketTable';
import { setUserMonthBonus, setStateAllUserMonthBonuses, setUserBonusMonthId } from '../actions';
import { getGraphQLErrorAsString } from '../../../utils/graphQLErrorHandlers';
import UserMonthBonusField from './UserMonthBonusField';
import { getUserBonusMonthId, getUserIdState } from '../selectors';
import { toDollarFormat } from '../../../utils';
import { UNAPPROVED, FINALIZED, APPROVED } from '../constants';

const propTypes = exact({
  validate: PropTypes.func.isRequired,
  allUserTicketBonusForms: PropTypes.array,

  fullname: PropTypes.string.isRequired,

  setUserMonthBonusAction: PropTypes.func.isRequired,
  setStateAllUserMonthBounsesAction: PropTypes.func.isRequired,

  user: PropTypes.object.isRequired,
  userBonusState: PropTypes.string.isRequired,

  userBonusMonthId: PropTypes.number,
  setUserBonusMonthIdAction: PropTypes.func.isRequired,

  totalOpportunity: PropTypes.number.isRequired,
  totalApproved: PropTypes.number.isRequired,
  totalAllPerDiems: PropTypes.number.isRequired,
  ...formPropTypes,
  ...formProps,
});

export const FORM_NAME = 'userTicketBonusForm';

const validate = (values) => {
  const errors = { members: [], _error: undefined };

  values.members.forEach((member) => {
    const memberErrors = {};
    const noNotes = (!member.manager_notes || member.manager_notes.length === 0);

    const isNotMax = (member.approved_bonus_amount < member.net_total_bonus_opportunity);
    const isOverMax = (member.approved_bonus_amount > member.net_total_bonus_opportunity);
    const isNotPresent = (member.approved_bonus_amount === null);

    /* NOTE: Added error message to approved_bonus_amount field, because error wouldn't display *
    *  unless field is touched but the error message is set on the notes field */

    if ((isNotMax) && noNotes) {
      memberErrors.approved_bonus_amount = 'Needs Reason';
    }
    if (isOverMax) {
      memberErrors.approved_bonus_amount = 'Over Max Amount';
    }
    if (isNotPresent) {
      memberErrors.approved_bonus_amount = 'Enter in a value';
    }

    errors.members.push(memberErrors);
  });

  return errors;
};

const renderMembers = ({ fields }) => {
  return (
    fields.map((memberPath) => {
      return (
        <UserMonthBonusField
          memberPath={memberPath}
          key={memberPath}
        />

      );
    })
  );
};


const UserBonusMonthForm = (props) => {
  const {
    handleSubmit,
    error,
  } = props;

  const {
    invalid, submitting,
  } = props;

  const {
    fullname,
    setUserMonthBonusAction,
    setStateAllUserMonthBounsesAction,
    userBonusState,
    totalOpportunity,
    totalApproved,
    totalAllPerDiems,
  } = props;


  const isFinalized = (userBonusState === FINALIZED);
  const renderFormButton = () => {
    if (userBonusState === APPROVED) {
      return (
        <Button
          bsStyle="warning"
          style={{ marginRight: '5px' }}
          type="submit"
        >
          Unapprove All
        </Button>
      );
    }
    return (
      <Button
        bsStyle="primary"
        style={{ marginRight: '5px' }}
        type="submit"
        disabled={(submitting || invalid)}
      >
        Approve All
      </Button>
    );
  };

  const onSubmit = async (values) => {
    const { members } = values;

    const userMonthBonusesForApproval = members.filter((member) => {
      return member.state !== FINALIZED;
    });

    const userMonthBonusUpdateInput = userMonthBonusesForApproval.map((item) => {
      return (
        _.pick(item, ['manager_notes', 'id', 'approved_bonus_amount'])
      );
    });

    const userMonthBonusApprovedIds = userMonthBonusesForApproval.map((userMonthBonus) => {
      return userMonthBonus.id;
    });

    if (userMonthBonusUpdateInput.length === 0) {
      return;
    }

    try {
      await setUserMonthBonusAction(userMonthBonusUpdateInput);

      const setNextState = (userBonusState === UNAPPROVED) ? APPROVED : UNAPPROVED;
      await setStateAllUserMonthBounsesAction(userMonthBonusApprovedIds, setNextState);
    } catch (e) {
      throw new SubmissionError({ _error: getGraphQLErrorAsString(e.graphQLErrors) });
    }
  };

  return (
    <React.Fragment>
      <div style={{ paddingBottom: '40px' }} className="panel panel-default">
        <div style={{ paddingLeft: '5px' }} className="panel-heading">
          <strong>Employee: </strong>
          {fullname}
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Table condensed>
            <thead>
              <tr>
                <th>District</th>
                <th>Department</th>
                <th>Total Billiable Hours</th>
                <th>Total Net Revenue</th>
                <th>Total Bonus Opportunity</th>
                <th>Total Per Diem</th>
                <th>Recommended Bonus Amount</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <FieldArray
                name="members"
                component={renderMembers}
              />
            </tbody>
            <tfoot>
              <tr>
                <td />
                <td />
                <td />
                <td />
                <td><strong> Total: {toDollarFormat(totalOpportunity)}</strong></td>
                <td><strong> Total:  {toDollarFormat(totalAllPerDiems)} </strong></td>
                <td><strong> Total:  {toDollarFormat(totalApproved)} </strong></td>
                <td />
              </tr>
            </tfoot>
          </Table>
          <ButtonToolbar bsClass="pull-right">
            {!isFinalized && renderFormButton()}
          </ButtonToolbar>
          {error
          && <span className="form-error"><strong>{error}</strong></span>}
        </form>
      </div>
      <UserBonusTicketTable />
    </React.Fragment>
  );
};


UserBonusMonthForm.propTypes = propTypes;

/* eslint-disable react/forbid-prop-types, react/require-default-props */
renderMembers.propTypes = exact({
  fields: PropTypes.object.isRequired,
  meta: PropTypes.object,
});
/* eslint-enable react/forbid-prop-types, react/require-default-props */


const withForm = reduxForm({
  form: FORM_NAME,
  validate,
});

const mapDispatchToProps = {
  setUserMonthBonusAction: setUserMonthBonus,
  setStateAllUserMonthBounsesAction: setStateAllUserMonthBonuses,
  setUserBonusMonthIdAction: setUserBonusMonthId,
};

const formValues = connect((state, ownProps) => {
  const { fullname } = ownProps.user;
  const userBonusState = getUserIdState(state);
  const userBonusMonthId = getUserBonusMonthId(state);

  const allMembers = ownProps.initialValues.members;
  const totalOpportunity = _.sumBy(allMembers, (member) => {
    return (member.total_bonus_opportunity) || 0;
  });

  const totalApproved = _.sumBy(allMembers, (member) => {
    return (member.approved_bonus_amount) || 0;
  });

  const totalAllPerDiems = _.sumBy(allMembers, (member) => {
    return (member.total_per_diem_amount) || 0;
  });

  return {
    fullname,
    userBonusState,
    userBonusMonthId,
    totalOpportunity,
    totalApproved,
    totalAllPerDiems,
  };
}, mapDispatchToProps);


const withInitPage = lifecycle({
  componentWillMount() {
    /* NOTE: for query params from the summary page */
    const { setUserBonusMonthIdAction, userBonusMonthId, initialValues } = this.props;
    const { members } = initialValues;
    const index = _.findIndex(members, (member) => {
      return (member.id === userBonusMonthId);
    });

    const selectedIndex = (index < 0) ? 0 : index;
    const { id: nextUserBonusMonthId } = this.props.initialValues.members[selectedIndex];
    setUserBonusMonthIdAction(nextUserBonusMonthId);
  },
});


const enhance = compose(
  withForm,
  formValues,
  withInitPage,
);

export default enhance(UserBonusMonthForm);
